import { useState } from "react";
import { Modal } from "react-bootstrap";
import { useApiMutation } from "../../utils/api";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { genericError } from "../../utils/errors";

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const STEP_MINUTES = 5;
const SKIP_MINUTES = 30;

interface LogActivityModalProps {
  model: string;
  close: () => void;
  refetch: () => void;
}

const titleize = (model: string): string => {
  return (
    model.charAt(0).toUpperCase() +
    model.slice(1).replace(/(?:_|-)([a-z])/g, (_, c) => " " + c.toUpperCase())
  );
};

export const LogActivityModal = ({
  model,
  close,
  refetch,
}: LogActivityModalProps) => {
  const { post, saving } = useApiMutation();
  const [customTime, setCustomTime] = useState<Date | null>(null);

  const getInitialTime = () => new Date();

  const selectedTime = customTime ?? getInitialTime();

  const adjustTime = (offset: number) => {
    const base = customTime ?? getInitialTime();
    const minutes = base.getMinutes();
    const sign = offset > 0 ? 1 : -1;
    const absOffset = Math.abs(offset);

    let remainder = minutes % absOffset;

    let nextDate: Date;
    if (remainder !== 0) {
      let adjustBy = 0;
      if (sign > 0) {
        adjustBy = absOffset - remainder;
      } else {
        adjustBy = remainder === 0 ? 0 : -remainder;
      }
      nextDate = new Date(base);
      nextDate.setMinutes(minutes + adjustBy);
    } else {
      nextDate = new Date(base);
      nextDate.setMinutes(minutes + offset);
    }
    nextDate.setSeconds(0, 0);
    setCustomTime(nextDate);
  };

  const handleSubmit = () => {
    post(`/api/user/create_${model}`, {
      ended_at: selectedTime.toISOString(),
    }).then(({ data }) => {
      if (data.success) {
        refetch();
        close();
      } else {
        genericError();
      }
    });
  };

  return (
    <Modal show onHide={close} size="lg" centered>
      <Modal.Header
        closeButton
        className="justify-content-center border-0 pb-0"
      >
        <Modal.Title className="w-100 text-center fs-32">
          Add {titleize(model)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center py-4">
        <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(-SKIP_MINUTES)}
          >
            <ChevronsLeft size={48} />
          </button>
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(-STEP_MINUTES)}
          >
            <ChevronLeft size={48} />
          </button>

          <div className="pt-4 pb-4 pl-3 pr-3">
            <div className="fs-64 fw-bold">{formatTime(selectedTime)}</div>
          </div>

          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(STEP_MINUTES)}
          >
            <ChevronRight size={48} />
          </button>
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(SKIP_MINUTES)}
          >
            <ChevronsRight size={48} />
          </button>
        </div>

        <div className="d-flex mt-3">
          <button
            type="button"
            className="btn btn-primary btn-lg px-5 mx-auto"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};
